import PageHeader from "@/custolink-views/asset_frontend/ui/layout/PageHeader";
import PageContent from "@/custolink-views/asset_frontend/ui/layout/PageContent";
import useAssetViewFacade from "@/custolink-views/asset_frontend/asset_view/useAssetViewFacade";
import Loading from "@/components/shared/Loading";
import AssetDescriptionForHeader from "@/custolink-views/asset_frontend/ui/components/AssetDescriptionForHeader";
import AssetHeaderComponents from "@/custolink-views/asset_frontend/ui/components/AssetHeaderComponents";
import { PublicAssetFrontendApi$AssetComponentDtoKey } from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import Drawer from "../../../components/ui/Drawer";
import RegisterProduct from "@/custolink-views/asset_frontend/asset_view/asset_components/RegisterProduct";
import Inquiry from "@/custolink-views/asset_frontend/asset_view/asset_components/Inquiry";
import AiSupportComponent from "@/custolink-views/asset_frontend/asset_view/asset_components/ai_support/AiSupportComponent";
import FileComponent from "@/custolink-views/asset_frontend/asset_view/asset_components/file_component/FileComponent";
import MiddleSection from "@/custolink-views/asset_frontend/ui/components/MiddleSection";
import BottomSection from "@/custolink-views/asset_frontend/ui/components/BottomSection";
import VideoCallAppPageComponent from "@/custolink-views/asset_frontend/asset_view/asset_components/VideoCallAppPageComponent";
import BottomNavigation from "@/custolink-views/asset_frontend/ui/building_blocks/BottomNavigation";
import AccountDrawer from "@/custolink-views/asset_frontend/account/AccountDrawer";
import AssetProtectedSignIn from "@/custolink-views/asset_frontend/asset_view/AssetProtectedSignIn";
import AssetDrawerScope from "@/custolink-views/asset_frontend/redesigns/AssetDrawerScope";
import { normaliseHex } from "@/custolink-views/asset_frontend/redesigns/theme";

import { visitAsset } from "@/custolink-views/asset_frontend/visited_assets";
import { useEffect, useState } from "react";
import useUiContext from "@/custolink-hooks/useUiContext";
import NudgeToDefaultViewDialog from "@/custolink-views/asset_frontend/asset_view/NudgeToDefaultViewDialog";
import VoiceCallAppPageComponent from "@/custolink-views/asset_frontend/asset_view/asset_components/VoiceCallAppPageComponent";
import { useNavigate } from "react-router-dom";
import Scan from "@/custolink-views/asset_frontend/scan/Scan";

export type AssetViewProps = {
  serialNumber: string;
  customAssetView: {
    urlSlug: string;
    assetAttrValue: string;
  };
};

export default function AssetViewClassic(p: AssetViewProps) {
  const uic = useUiContext();
  const assetViewFacade = useAssetViewFacade(
    p.serialNumber,
    p.customAssetView.urlSlug,
    p.customAssetView.assetAttrValue,
  );
  const [markAsVisited, setMarkAsVisited] = useState(0);
  const [nudgeDecision, setNudgeDecision] = useState<boolean | null>(null); // null, untill decided
  const navigate = useNavigate();

  useEffect(() => {
    if (assetViewFacade.details != null) {
      if (
        assetViewFacade.details.inferredCategoryView?.uiNudgeToInputSerial ===
        true
      ) {
        // before marking visit: must nudge to default view, or user must decide to continue
        if (nudgeDecision == false) {
          setMarkAsVisited(1);
        }
      } else {
        setMarkAsVisited(1);
      }
    }
  }, [assetViewFacade?.details, nudgeDecision]);

  useEffect(() => {
    if (markAsVisited == 1 && assetViewFacade.details != null) {
      visitAsset(uic._orgId, assetViewFacade.details);
    }
  }, [uic._orgId, markAsVisited, assetViewFacade.details]);

  if (assetViewFacade.isRequestPending) {
    return <Loading loading={true} />;
  }
  // Asset Protection hides this product from the visitor: no serial, product
  // name or components came back. The protected card takes the place of the
  // asset card on the brand band; details is null, so no visit is recorded.
  if (assetViewFacade.protectedSignInRequired) {
    return (
      <div>
        <PageHeader>
          <AssetProtectedSignIn
            variant="classic"
            onSignIn={() => assetViewFacade.showAccountView()}
          />
        </PageHeader>

        <PageContent>
          <BottomNavigation
            onClickHome={() => {
              assetViewFacade.hideViewFunctionOrDrawer();
              navigate("/");
            }}
            onClickAccount={() => {
              assetViewFacade.showAccountView();
            }}
            onClickScan={() => {
              assetViewFacade.showScanView();
            }}
          />
        </PageContent>

        <AccountDrawer
          open={
            assetViewFacade.assetViewFunction == "Account" ||
            assetViewFacade.assetViewFunction == "CreateAccount"
          }
          useThisFeatureAlert={
            assetViewFacade.assetViewFunction == "CreateAccount"
          }
          close={() => assetViewFacade.hideViewFunctionOrDrawer()}
        />

        <Scan
          open={assetViewFacade.assetViewFunction == "Scan"}
          close={() => assetViewFacade.hideViewFunctionOrDrawer()}
        />
      </div>
    );
  }
  if (
    assetViewFacade.details == null ||
    assetViewFacade.assetViewOrAssetNotFound
  ) {
    return (
      <div>
        <PageHeader>
          <p>Sorry</p>
        </PageHeader>
        <PageContent>
          <p>The asset could not be found.</p>
        </PageContent>
      </div>
    );
  }

  return (
    <div>
      <PageHeader>
        <AssetDescriptionForHeader viewDetails={assetViewFacade.details} />
        <AssetHeaderComponents
          components={assetViewFacade.details.components.topSection}
          onClickComponent={assetViewFacade.selectComponent}
        />
      </PageHeader>

      <PageContent>
        <MiddleSection
          components={assetViewFacade.details.components.middleSection}
          selectComponentToShow={assetViewFacade.selectComponent}
        />

        <BottomSection
          components={assetViewFacade.details.components.bottomSection}
          selectComponent={assetViewFacade.selectComponent}
        />

        {assetViewFacade.selectedComponent == null && (
          <BottomNavigation
            onClickHome={() => {
              assetViewFacade.hideViewFunctionOrDrawer();
              navigate("/");
            }}
            onClickAccount={() => {
              assetViewFacade.showAccountView();
            }}
            onClickScan={() => {
              assetViewFacade.showScanView();
            }}
          />
        )}
      </PageContent>

      <AccountDrawer
        open={
          assetViewFacade.assetViewFunction == "Account" ||
          assetViewFacade.assetViewFunction == "CreateAccount"
        }
        useThisFeatureAlert={
          assetViewFacade.assetViewFunction == "CreateAccount"
        }
        close={() => assetViewFacade.hideViewFunctionOrDrawer()}
      />

      <Scan
        open={assetViewFacade.assetViewFunction == "Scan"}
        close={() => assetViewFacade.hideViewFunctionOrDrawer()}
      />

      <Drawer
        title={assetViewFacade.selectedComponent?.name ?? ""}
        isOpen={assetViewFacade.selectedComponent != null}
        placement="right"
        width={"100%"}
        bodyClass={
          assetViewFacade.selectedComponent?.key ==
          PublicAssetFrontendApi$AssetComponentDtoKey.VideoCall
            ? "pl-1 pr-1 pt-1 pb-1"
            : ""
        }
        onClose={() => {
          assetViewFacade.hideViewFunctionOrDrawer();
        }}
        onRequestClose={() => {
          assetViewFacade.hideViewFunctionOrDrawer();
        }}
      >
        {assetViewFacade.selectedComponent != null && (
          <AssetDrawerScope
            accent={normaliseHex(
              uic.organisationContext.lookAndFeel.assetFrontendBgcolorHex,
            )}
          >
            {assetViewFacade.selectedComponent.filesystemComponent && (
              <FileComponent
                assetViewDetails={assetViewFacade.details}
                assetComponent={assetViewFacade.selectedComponent}
                onSignIn={() => assetViewFacade.showAccountView()}
              />
            )}

            {!assetViewFacade.selectedComponent.filesystemComponent && (
              <>
                {assetViewFacade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.RegisterProduct && (
                  <RegisterProduct
                    componentId={assetViewFacade.selectedComponent.id}
                    assetView={assetViewFacade.details}
                    onDone={() => assetViewFacade.hideViewFunctionOrDrawer()}
                  />
                )}
                {assetViewFacade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.Inquiry && (
                  <Inquiry
                    componentId={assetViewFacade.selectedComponent.id}
                    assetView={assetViewFacade.details}
                    onDone={() => assetViewFacade.hideViewFunctionOrDrawer()}
                  />
                )}
                {assetViewFacade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.AiSupport && (
                  <AiSupportComponent
                    assetViewDetails={assetViewFacade.details}
                  />
                )}
                {assetViewFacade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.VideoCall && (
                  <>
                    <VideoCallAppPageComponent
                      component={assetViewFacade.selectedComponent}
                      assetView={assetViewFacade.details}
                      onUserRequestsCloseCall={
                        assetViewFacade.hideViewFunctionOrDrawer
                      }
                    />
                  </>
                )}
                {assetViewFacade.selectedComponent.key ==
                  PublicAssetFrontendApi$AssetComponentDtoKey.VoiceCall && (
                  <VoiceCallAppPageComponent
                    component={assetViewFacade.selectedComponent}
                  />
                )}
              </>
            )}
          </AssetDrawerScope>
        )}
      </Drawer>

      <NudgeToDefaultViewDialog
        show={
          nudgeDecision == null &&
          assetViewFacade.details.inferredCategoryView != null &&
          assetViewFacade.details.inferredCategoryView.uiNudgeToInputSerial
        }
        assetViewDetails={assetViewFacade.details}
        onDenyNudge={() => setNudgeDecision(false)}
      />
    </div>
  );
}
